/**
 * Delivery Edit Page
 * Form for editing an existing delivery
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type { Delivery, UpdateDeliveryRequest } from '@/modules/commerce/delivery/types/delivery';
import { DELIVERY_STATUSES } from '@/modules/commerce/delivery/types/delivery';
import { getDeliveryById, updateDelivery } from '@/services/commerce/deliveryService';

export default function DeliveryEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const initialDelivery = (location.state as { delivery?: Delivery })?.delivery || null;

  const [loading, setLoading] = useState(!initialDelivery);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<UpdateDeliveryRequest>({
    status: initialDelivery?.status || 'PENDING',
    deliveryAddress: initialDelivery?.deliveryAddress || '',
    vehicleNumber: initialDelivery?.vehicleNumber || '',
    driverName: initialDelivery?.driverName || '',
    driverPhone: initialDelivery?.driverPhone || '',
    notes: initialDelivery?.notes || '',
    currentLocation: initialDelivery?.currentLocation || '',
    statusReason: initialDelivery?.statusReason || '',
    contactPerson: initialDelivery?.contactPerson || '',
    contactPhone: initialDelivery?.contactPhone || '',
  });

  const [deliveryInfo, setDeliveryInfo] = useState<Partial<Delivery>>({
    id: initialDelivery?.id,
    deliveryNumber: initialDelivery?.deliveryNumber,
    externalId: initialDelivery?.externalId,
    salesOrderId: initialDelivery?.salesOrderId,
    assignedFrom: initialDelivery?.assignedFrom,
  });

  useEffect(() => {
    if (!initialDelivery && id) {
      fetchDelivery(parseInt(id, 10));
    }
  }, [id, initialDelivery]);

  const fetchDelivery = async (deliveryId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDeliveryById(deliveryId);
      setFormData({
        status: data.status,
        deliveryAddress: data.deliveryAddress || '',
        vehicleNumber: data.vehicleNumber || '',
        driverName: data.driverName || '',
        driverPhone: data.driverPhone || '',
        notes: data.notes || '',
        currentLocation: data.currentLocation || '',
        statusReason: data.statusReason || '',
        contactPerson: data.contactPerson || '',
        contactPhone: data.contactPhone || '',
      });
      setDeliveryInfo({
        id: data.id,
        deliveryNumber: data.deliveryNumber,
        externalId: data.externalId,
        salesOrderId: data.salesOrderId,
        assignedFrom: data.assignedFrom,
      });
    } catch (err: any) {
      console.error('Failed to fetch delivery:', err);
      setError(err.message || 'Failed to load delivery details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/sales/delivery');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
    setSuccessMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) {
      setError('Delivery ID is missing');
      return;
    }

    // Basic validation
    if (!formData.driverName?.trim()) {
      setError('Driver name is required');
      return;
    }

    if (!formData.driverPhone?.trim()) {
      setError('Driver phone is required');
      return;
    }

    if (!formData.deliveryAddress?.trim()) {
      setError('Delivery address is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await updateDelivery(parseInt(id, 10), formData);
      setSuccessMessage('Delivery updated successfully!');

      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/sales/delivery', { state: { refresh: true } });
      }, 1500);
    } catch (err: any) {
      console.error('Failed to update delivery:', err);
      setError(err.message || 'Failed to update delivery');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading delivery details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Deliveries
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            Edit Delivery {deliveryInfo.deliveryNumber || deliveryInfo.externalId || `#${deliveryInfo.id}`}
          </h1>
          <p className="text-gray-600 mt-1">Update delivery information</p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-sm text-blue-600">Sales Order:</span>
              <span className="ml-2 font-medium text-blue-800">{deliveryInfo.salesOrderId}</span>
            </div>
            <div>
              <span className="text-sm text-blue-600">Source:</span>
              <span className={`ml-2 px-2 py-1 rounded text-sm font-medium ${
                deliveryInfo.assignedFrom === 'UrjaVyapar'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-orange-100 text-orange-800'
              }`}>
                {deliveryInfo.assignedFrom || 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {successMessage}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {DELIVERY_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status Reason
              </label>
              <input
                type="text"
                name="statusReason"
                value={formData.statusReason}
                onChange={handleChange}
                placeholder="Reason for status change"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Driver Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driver Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="driverName"
                value={formData.driverName}
                onChange={handleChange}
                placeholder="Enter driver name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Driver Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driver Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                inputMode="numeric"
                name="driverPhone"
                value={formData.driverPhone}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleChange({ ...e, target: { ...e.target, name: 'driverPhone', value: digits } } as React.ChangeEvent<HTMLInputElement>);
                }}
                placeholder="10-digit phone number"
                pattern="[0-9]{10}"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Vehicle Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle Number
              </label>
              <input
                type="text"
                name="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
                placeholder="e.g., MH12AB1234"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Current Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Location
              </label>
              <input
                type="text"
                name="currentLocation"
                value={formData.currentLocation}
                onChange={handleChange}
                placeholder="Current delivery location"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Contact Person */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                placeholder="Recipient contact name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Contact Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Phone
              </label>
              <input
                type="tel"
                inputMode="numeric"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleChange({ ...e, target: { ...e.target, name: 'contactPhone', value: digits } } as React.ChangeEvent<HTMLInputElement>);
                }}
                placeholder="Recipient phone number"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Delivery Address - Full Width */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="deliveryAddress"
                value={formData.deliveryAddress}
                onChange={handleChange}
                placeholder="Full delivery address"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Notes - Full Width */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Additional notes or instructions"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
